import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, combineLatest, map, of, startWith, switchMap } from 'rxjs';
import { SharedModule } from '@shared/shared.module';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { AccountingDeParaGovernanceService } from '@app/core/services/http/accounting-data-standardization/accounting-de-para-governance.service';
import {
  AccountingExceptionQueue,
  AccountingExceptionQueueItem,
} from '@interfaces/accounting-data-standardization/responses/exception-queue.response';
import { describeBackendError } from '@src/app/features/dashboard/accounting-data-standardization/utils/backend-error.util';
import {
  exceptionRuleLabel,
  formatAgeDays,
  formatAverageAgeDays,
} from '@src/app/features/dashboard/accounting-data-standardization/utils/exception-queue.util';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Estado da carga: os dados, ou o erro com a mensagem do backend. */
type QueueState =
  | { status: 'loading' }
  | { status: 'loaded'; queue: AccountingExceptionQueue }
  | { status: 'error'; message: string };

/** Pedido para abrir um documento da fila no detalhe da Padronização. */
export interface ExceptionQueueOpenRequest {
  companyId: string;
  extractionId: string;
}

/**
 * Aba "Retidos" da listagem da Padronização (PBI 3423, DPA-12): a fila de exceção medida. Mostra, para as empresas do
 * filtro da tela, quantos documentos de Estrutura Contábil estão retidos por regra (com a idade média) e a lista
 * (empresa, documento, motivo, há quanto tempo). Clicar num resumo filtra a lista pela regra; clicar num documento abre
 * o detalhe (`openRequested`).
 */
@Component({
  selector: 'app-exception-queue-panel',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './exception-queue-panel.component.html',
  styleUrl: './exception-queue-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExceptionQueuePanelComponent {
  private readonly governance = inject(AccountingDeParaGovernanceService);
  private readonly dateFormatter = inject(DateFormatterService);

  /** Empresas do filtro da listagem (vazio = todas do escopo). */
  readonly companyIds = input<string[]>([]);
  readonly openRequested = output<ExceptionQueueOpenRequest>();

  /** Regra escolhida no resumo (nulo = todas). Filtro só da tela: o resumo continua com todas as regras. */
  readonly selectedRule = signal<string | null>(null);
  /** Incrementado pelo "Atualizar" para buscar de novo. */
  private readonly reloadTick = signal(0);

  private readonly state = toSignal(
    combineLatest([toObservable(this.companyIds), toObservable(this.reloadTick)]).pipe(
      switchMap(([companyIds]) =>
        this.governance.getExceptionQueue(companyIds).pipe(
          map(
            (queue): QueueState => ({
              status: 'loaded',
              queue: { summary: queue?.summary ?? [], items: queue?.items ?? [] },
            })
          ),
          catchError((err: unknown) =>
            of<QueueState>({
              status: 'error',
              message: describeBackendError(
                err,
                'Não foi possível carregar os documentos retidos. Use Atualizar para tentar de novo.'
              ),
            })
          ),
          startWith<QueueState>({ status: 'loading' })
        )
      )
    ),
    { initialValue: { status: 'loading' } as QueueState }
  );

  readonly isLoading = computed(() => this.state().status === 'loading');
  readonly errorMessage = computed(() => {
    const state = this.state();
    return state.status === 'error' ? state.message : null;
  });

  /** Resumo por regra, mais retidas primeiro, com rótulo e idade média prontos. */
  readonly summary = computed(() => {
    const state = this.state();
    if (state.status !== 'loaded') return [];
    return [...state.queue.summary]
      .sort((a, b) => b.count - a.count)
      .map(item => ({
        rule: item.rule,
        label: exceptionRuleLabel(item.rule),
        count: item.count,
        averageAge: formatAverageAgeDays(item.averageAgeDays),
      }));
  });

  readonly total = computed(() => {
    const state = this.state();
    return state.status === 'loaded' ? state.queue.items.length : 0;
  });

  /** Lista filtrada pela regra escolhida, mais antigos primeiro. */
  readonly rows = computed(() => {
    const state = this.state();
    if (state.status !== 'loaded') return [];
    const rule = this.selectedRule();
    return state.queue.items
      .filter(item => !rule || item.rule === rule)
      .map(item => ({ item, ageDays: this.ageDaysOf(item) }))
      .sort((a, b) => (b.ageDays ?? -1) - (a.ageDays ?? -1))
      .map(({ item, ageDays }) => ({
        item,
        ruleLabel: exceptionRuleLabel(item.rule),
        age: formatAgeDays(ageDays),
      }));
  });

  toggleRule(rule: string): void {
    this.selectedRule.update(current => (current === rule ? null : rule));
  }

  reload(): void {
    this.reloadTick.update(tick => tick + 1);
  }

  open(item: AccountingExceptionQueueItem): void {
    this.openRequested.emit({ companyId: item.companyId, extractionId: item.extractionId });
  }

  /** Idade em dias: a do backend quando vem pronta; senão, a partir de `since`. */
  private ageDaysOf(item: AccountingExceptionQueueItem): number | null {
    if (item.ageDays != null) return item.ageDays;
    const since = this.dateFormatter.parseDate(item.since);
    if (!since) return null;
    return (this.dateFormatter.getNowTimestamp() - since.getTime()) / DAY_MS;
  }
}
